import React from 'react';
import classnames from 'classnames';
import { renderRoutes } from 'react-router-config';
import { RouteComponentProps } from 'react-router';
import DaApp from '@wix/da-shared-react/pkg/DaApp';
import { RuntimeEnvironment } from '@wix/da-shared-react/pkg/types';
import { PerspectiveProvider } from '@wix/da-shared-react/pkg/Context/PerspectiveContext';
import { SectionProvider } from '../Sections/SectionContext';
import { HeaderBehavior } from '@wix/da-shared-react/pkg/types/headerBehavior';
import { ThemeOptions } from '@wix/da-shared-react/pkg/publicSession/types';
import SiteHeader from '@wix/da-shared-react/pkg/SiteHeader';
import SiteFooter from '@wix/da-shared-react/pkg/SiteFooter';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import ProfileIdentity from '../UserWidgets/ProfileIdentity';
import MoreActionsDropdownMobile from '../UserWidgets/ProfileIdentity/MoreActionsDropdown/Mobile';
import ProfileHero, { INITIAL_SCROLL_TOP } from '../UserWidgets/ProfileHero';
import ProfileHeroEdit from '../UserWidgets/ProfileHero/ProfileHeroEdit';
import ProfileHeroIntroPopup from '../UserWidgets/ProfileHero/ProfileHeroIntroPopup';
import SubNavBar from '../SubNavBar';
import BackToTop from '@wix/da-shared-react/pkg/BackToTop';
import { BiLoggerContextProvider } from '@wix/da-shared-react/pkg/biLogger';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';

import ProfileHeroFTUE from '../UserWidgets/ProfileHero/ProfileHeroFTUE';

import { routes, sectionRoutes } from '../../routes';

import s from './App.scss';

export const ZERO_PERSPECTIVE_CONTAINER_ID = 'zero-perspective-container';
export const PERSPECTIVE_CONTAINER_ID = 'perspective-container';
export const CONTENT_CONTAINER_ID = 'content-container';

export interface Props {
  pageTitle: string;
  pageDescription?: string;
  activeSection: string;
  seoCanonical: string;
  language: string;
  isMobile: boolean;
  isOwner?: boolean;
  profileOwnerId: number;
  isSectionLoading?: boolean;
  theme?: ThemeOptions;
  userAgent?: string;
  environment?: RuntimeEnvironment;
  hasVerticalCoverImage: boolean;
  showProfileHeroAid?: boolean;
  showProfileEditAid?: boolean;
  onAppRendered(): void;
  onPathChange(location: object): void;
  isReducedHero?: boolean;
}

export interface State {
  isIntroPopupOpen: boolean;
}

class App extends React.Component<Props & RouteComponentProps<any>, State> {
  static defaultProps = {
    language: 'en',
  };

  state = {
    isIntroPopupOpen: false,
  };

  perspectiveContainer: React.RefObject<any> = React.createRef();
  pageContentContainer: React.RefObject<any> = React.createRef();
  sectionContentContainer: React.RefObject<any> = React.createRef();

  private doneShowingEditControls = false;

  componentDidMount() {
    this.props.onAppRendered();
    const pageContainerClassList = this.pageContentContainer.current!.classList;
    pageContainerClassList.add(s['show-edit-controls']);
    // on SSR rendering we show edit controls
    // and hide them after 2 seconds here using direct node manipulation
    // to be make it more performant
    // we also need to set this.doneShowingEditControls so that
    // a subsequent re-render doesn't make them re-appear
    setTimeout(() => {
      pageContainerClassList.remove(s['show-edit-controls']);
      this.doneShowingEditControls = true;
      // this is not done via state to not re-render App.tsx in a bit after page load
      // while the user is likely to be scrolling
    }, 2000);
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (
      nextProps.theme !== this.props.theme ||
      nextProps.hasVerticalCoverImage !== this.props.hasVerticalCoverImage ||
      nextProps.showProfileHeroAid !== this.props.showProfileHeroAid ||
      nextProps.showProfileEditAid !== this.props.showProfileEditAid ||
      nextState.isIntroPopupOpen !== this.state.isIntroPopupOpen
    ) {
      return true;
    }

    const section = this.props.location.pathname.split('/')[2];
    if (['gallery', 'favourites'].includes(section)) {
      const nextSection = nextProps.location.pathname.split('/')[2];
      if (section !== nextSection) {
        this.props.onPathChange(nextProps.location);
      }
    } else {
      // Using not full url to make navigation to <a name /> by hash works
      // or passing params to section data loader
      if (nextProps.location.pathname !== this.props.location.pathname) {
        this.props.onPathChange(nextProps.location);
      }
    }

    return this.props.isSectionLoading !== nextProps.isSectionLoading;
  }

  componentDidUpdate(prevProps) {
    if (!this.props.isSectionLoading && prevProps.isSectionLoading) {
      const isTopOfSectionVisible =
        this.sectionContentContainer.current.getBoundingClientRect().top > 0;

      if (!isTopOfSectionVisible) {
        this.perspectiveContainer.current.scrollTop = INITIAL_SCROLL_TOP;
      }
    }
  }

  render() {
    const {
      isMobile,
      pageTitle,
      language,
      environment,
      activeSection,
      seoCanonical: pageUrl,
      pageDescription,
      hasVerticalCoverImage,
      showProfileHeroAid,
      showProfileEditAid,
      profileOwnerId,
      isReducedHero,
    } = this.props;

    return (
      <DaApp
        headTags={{
          pageTitle,
          pageDescription,
          language,
          environment,
          pageUrl,
        }}
      >
        <PerspectiveProvider
          isParallaxEnabled={this.getIsParallaxEnabled()}
          zeroPerspectiveContainerId={ZERO_PERSPECTIVE_CONTAINER_ID}
          scrollableContainerId={
            this.getIsParallaxEnabled() ? PERSPECTIVE_CONTAINER_ID : null
          }
          contentContainerId={CONTENT_CONTAINER_ID}
        >
          <SectionProvider value={activeSection}>
            <BiLoggerContextProvider
              value={{
                view: 'profile',
                component: activeSection,
                typeid: ResourceType.USER,
                itemid: profileOwnerId,
              }}
            >
              <div id={ZERO_PERSPECTIVE_CONTAINER_ID} />
              <div
                id={PERSPECTIVE_CONTAINER_ID}
                ref={this.perspectiveContainer}
                className={classnames(
                  s['perspective-container'],
                  hasVerticalCoverImage && s['perspective-origin-top'],
                  !this.getIsParallaxEnabled() && s['no-parallax']
                )}
              >
                <SiteHeader
                  environment={environment}
                  activeMainMenuItem={null}
                  mobileBehavior={HeaderBehavior.STICKY}
                />
                <ProfileHero
                  isMobile={isMobile}
                  showProfileHeroAid={showProfileHeroAid}
                  showProfileEditAid={showProfileEditAid}
                  className={classnames(
                    s['profile-hero'],
                    isReducedHero && s['profile-hero-reduced']
                  )}
                />
                {this.state.isIntroPopupOpen && (
                  <ProfileHeroIntroPopup onClose={this.handleCloseIntroPopup} />
                )}
                <div
                  className={classnames(
                    s['above-hero-content'],
                    isReducedHero && s['reduced-hero']
                  )}
                >
                  {this.renderContent()}
                  <SiteFooter />
                </div>
                {/* da-user-profiles has <BackToTop /> injected here instead of in DaApp due to
                    scroll listener conflicts with PerspectiveProvider context */}
                <BackToTop scrollToY={isMobile ? 0 : INITIAL_SCROLL_TOP} />
              </div>
            </BiLoggerContextProvider>
          </SectionProvider>
        </PerspectiveProvider>
      </DaApp>
    );
  }

  renderContent() {
    const showEditControls =
      !this.doneShowingEditControls && this.props.isOwner;
    const { showProfileHeroAid, showProfileEditAid } = this.props;
    return (
      <div
        id={CONTENT_CONTAINER_ID}
        ref={this.pageContentContainer}
        className={classnames(
          s['content-container'],
          showEditControls && s['show-edit-controls'],
          showProfileHeroAid && s['show-ftue-controls']
        )}
      >
        <div className={s['profile-hero-overlay']} />
        <div className={s['content-container-with-paddings']}>
          {showProfileHeroAid && (
            <ProfileHeroFTUE
              className={s['profile-hero-ftue']}
              openIntroPopup={this.handleOpenIntroPopup}
              showProfileEditAid={showProfileEditAid}
            />
          )}
          <ErrorBoundary>
            <div className={s['profile-identity-container']}>
              <ProfileIdentity className={s['profile-identity']} />
              {this.props.isOwner && !this.props.isMobile && (
                <ProfileHeroEdit
                  className={classnames(
                    s['profile-hero-edit'],
                    showProfileHeroAid && s['show-ftue-controls']
                  )}
                  openIntroPopup={this.handleOpenIntroPopup}
                  showProfileHeroAid={showProfileHeroAid}
                  showProfileEditAid={showProfileEditAid}
                />
              )}
              {this.props.isMobile && (
                <MoreActionsDropdownMobile className={s['more-actions']} />
              )}
            </div>
          </ErrorBoundary>
          <SubNavBar routes={sectionRoutes} />
          <div
            ref={this.sectionContentContainer}
            className={s['section-container']}
          >
            {renderRoutes(routes)}
          </div>
        </div>
      </div>
    );
  }

  handleOpenIntroPopup = () => {
    this.setState({ isIntroPopupOpen: true });
  };

  handleCloseIntroPopup = () => {
    this.setState({ isIntroPopupOpen: false });
  };

  getIsParallaxEnabled() {
    const { isMobile, isReducedHero, userAgent } = this.props;
    if (isReducedHero) {
      return false;
    }
    if (isMobile) {
      return false;
    }

    if (userAgent) {
      return !/edg([ea]|ios)/i.test(userAgent);
    }

    return true;
  }
}

export default App;
